/* eslint-disable react/no-unknown-property */
"use client";
import Link from "next/link";
import React from "react";

import { IconRenderer } from "./IconRenderer";

export interface EducationCard {
  title?: string;
  description?: string;
  subtitle?: string; // For large cards
  link?: string;
  iconName?: string;
  gradientFrom?: string;
  gradientVia?: string;
  gradientTo?: string;
  backgroundImageUrl?: string;
  ctaLabel?: string;
  ctaLink?: string;
}

export interface KnowledgeHubCard {
  title?: string;
  subtitle?: string;
  backgroundImageUrl?: string;
  gradientFrom?: string;
  gradientVia?: string;
  gradientTo?: string;
  ctaLabel?: string;
  ctaLink?: string;
}

export interface EducationSectionProps {
  heading?: string;
  description?: string;
  knowledgeHubCards?: KnowledgeHubCard[];
  cards?: EducationCard[];
}

function renderIcon(iconName?: string) {
  return (
    <IconRenderer
      name={iconName || "book-open"}
      className="size-6 text-violet-950"
    />
  );
}

function toTitleCase(str: string): string {
  if (!str) return str;
  // Words that should remain lowercase (articles, prepositions, conjunctions)
  const lowercaseWords = [
    "a",
    "an",
    "and",
    "as",
    "at",
    "but",
    "by",
    "for",
    "from",
    "in",
    "into",
    "nor",
    "of",
    "on",
    "or",
    "the",
    "to",
    "with",
  ];

  return str
    .toLowerCase()
    .split(" ")
    .map((word, index) => {
      // Always capitalize first word, and capitalize if not in lowercaseWords list
      if (index === 0 || !lowercaseWords.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(" ");
}

/**
 * Education Entry Point Section
 * Shows Trichos as a knowledge centre, not just a sales clinic
 */
export default function EducationSection({
  heading = "Learn About Hair Loss",
  description = "Medical Knowledge & Patient Education",
  knowledgeHubCards,
  cards,
}: EducationSectionProps) {
  const educationTopics =
    cards && cards.length > 0
      ? cards
      : [
          {
            iconName: "FaBook",
            title: "Causes of Hair Loss",
            description: "Understanding the root causes",
            link: "/blog/hair-loss-causes",
          },
          {
            iconName: "FaQuestionCircle",
            title: "Types of Alopecia",
            description: "Learn about different conditions",
            link: "/blog/alopecia-types",
          },
          {
            iconName: "FaInfoCircle",
            title: "Surgery Day & Aftercare",
            description: "What to expect during treatment",
            link: "/blog/aftercare",
          },
          {
            iconName: "FaShieldAlt",
            title: "Consent & Safety Information",
            description: "Important safety guidelines",
            link: "/blog/safety",
          },
        ];

  // Duplicate all cards multiple times for truly seamless infinite scroll
  // We duplicate 3 times to ensure smooth continuous movement
  const loopedTopics = [
    ...educationTopics,
    ...educationTopics,
    ...educationTopics,
  ];

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = React.useState(0);
  const [trackWidth, setTrackWidth] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartX, setDragStartX] = React.useState(0);
  const [dragStartOffset, setDragStartOffset] = React.useState(0);
  const [scrollDirection, setScrollDirection] = React.useState<
    "left" | "right"
  >("left");
  const lastDragXRef = React.useRef(0);
  const [isSmallCardsHovered, setIsSmallCardsHovered] = React.useState(false);
  const [touchStartX, setTouchStartX] = React.useState(0);
  const [touchStartY, setTouchStartY] = React.useState(0);
  const [touchStartOffset, setTouchStartOffset] = React.useState(0);
  const [isTouching, setIsTouching] = React.useState(false);

  React.useEffect(() => {
    if (!trackRef.current) return;
    const width = trackRef.current.scrollWidth / 3; // width of one logical set
    setTrackWidth(width);
  }, [educationTopics.length]);

  // Auto-scrolling marquee logic (paused while dragging, direction changes based on drag)
  React.useEffect(() => {
    if (!trackWidth || isDragging || isTouching) return;

    let animationFrame: number;

    const step = () => {
      setOffset((prev) => {
        const speed = 0.5; // speed in px per frame
        const next =
          scrollDirection === "left"
            ? prev - speed // moving left (cards appear from right)
            : prev + speed; // moving right (cards appear from left)

        // Handle loop bounds based on direction
        if (scrollDirection === "left") {
          // When we've moved one full set width to the left, reset to 0
          if (Math.abs(next) >= trackWidth) {
            return 0;
          }
        } else {
          // When we've moved one full set width to the right, reset to 0
          if (next >= 0) {
            return -trackWidth;
          }
        }
        return next;
      });
      animationFrame = window.requestAnimationFrame(step);
    };

    animationFrame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [trackWidth, isDragging, isTouching, scrollDirection]);

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartOffset(offset);
    lastDragXRef.current = e.clientX;
  };

  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX;
      const newOffset = dragStartOffset + deltaX;
      setOffset(newOffset);

      // Detect drag direction and update scroll direction
      const currentX = e.clientX;
      const dragDelta = currentX - lastDragXRef.current;
      if (Math.abs(dragDelta) > 2) {
        // Threshold to avoid jitter
        if (dragDelta > 0) {
          setScrollDirection("right"); // Dragging right, cards move right
        } else if (dragDelta < 0) {
          setScrollDirection("left"); // Dragging left, cards move left
        }
        lastDragXRef.current = currentX;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Normalize offset when drag ends to keep it within loop bounds
      if (trackWidth > 0) {
        setOffset((prev) => {
          const normalized = prev % trackWidth;
          return normalized >= 0 ? normalized - trackWidth : normalized;
        });
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartOffset, trackWidth]);

  const handleMouseLeave = () => {
    if (isDragging && trackWidth > 0) {
      // Normalize offset when mouse leaves
      setOffset((prev) => {
        const normalized = prev % trackWidth;
        return normalized >= 0 ? normalized - trackWidth : normalized;
      });
    }
    setIsDragging(false);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);
    setTouchStartOffset(offset);
    setIsTouching(true);
    lastDragXRef.current = touch.clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isTouching) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartX);
    const deltaY = Math.abs(touch.clientY - touchStartY);

    // Only prevent default if horizontal movement is greater than vertical
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }

    const newDeltaX = touch.clientX - touchStartX;
    const newOffset = touchStartOffset + newDeltaX;
    setOffset(newOffset);

    // Detect swipe direction
    const currentX = touch.clientX;
    const swipeDelta = currentX - lastDragXRef.current;
    if (Math.abs(swipeDelta) > 2) {
      if (swipeDelta > 0) {
        setScrollDirection("right");
      } else if (swipeDelta < 0) {
        setScrollDirection("left");
      }
      lastDragXRef.current = currentX;
    }
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
    // Normalize offset when touch ends
    if (trackWidth > 0) {
      setOffset((prev) => {
        const normalized = prev % trackWidth;
        return normalized >= 0 ? normalized - trackWidth : normalized;
      });
    }
  };

  function getGradientClasses(index: number) {
    const variants = [
      ["from-violet-900", "via-violet-800", "to-rose-600"],
      ["from-sky-900", "via-sky-800", "to-cyan-500"],
      ["from-emerald-900", "via-emerald-800", "to-teal-500"],
      ["from-slate-900", "via-slate-800", "to-indigo-500"],
    ];

    const [from, via, to] = variants[index % variants.length];
    return { from, via, to };
  }

  const handleScroll = (direction: "left" | "right") => {
    // Arrow navigation - move one card at a time into view
    if (!trackWidth || !trackRef.current) return;

    // Change scroll direction first
    setScrollDirection(direction);

    // Calculate one card width + gap
    const firstCard = trackRef.current.children[0] as HTMLElement;
    if (!firstCard) return;

    const cardWidth = firstCard.offsetWidth;
    const gap = 16; // gap-4 = 1rem = 16px (matches the gap-2 md:gap-3 lg:gap-4)
    const moveDistance = cardWidth + gap;

    setOffset((prev) => {
      const next =
        direction === "left" ? prev + moveDistance : prev - moveDistance;

      // Wrap within loop bounds for seamless continuation
      // Since cards are duplicated 3x, we can safely wrap at trackWidth
      if (direction === "left") {
        if (next >= 0) {
          return next - trackWidth; // Wrap seamlessly
        }
      } else {
        if (Math.abs(next) >= trackWidth) {
          return next + trackWidth; // Wrap seamlessly
        }
      }
      return next;
    });
  };

  const renderEducationCard = (
    topic: EducationCard,
    index: number,
    isLarge = false
  ) => {
    if (!topic) return null;

    const fallback = getGradientClasses(index);
    const fromClass = topic.gradientFrom || fallback.from;
    const viaClass = topic.gradientVia || fallback.via;
    const toClass = topic.gradientTo || fallback.to;

    const baseClasses = isLarge
      ? [
          "group relative flex flex-col rounded-3xl",
          "h-[320px] md:h-[580px] w-full",
          "overflow-hidden",
          "shadow-md transition-transform duration-300 ease-out",
          "flex-shrink-0",
        ].join(" ")
      : [
          "group flex flex-col justify-end rounded-3xl",
          "bg-gradient-to-br",
          fromClass,
          viaClass,
          toClass,
          // Responsive card size: adjusted to match title size
          "h-[100px] w-[160px] md:h-[220px] md:w-[350px] lg:h-[250px] lg:w-[400px]",
          "p-3 md:p-5 lg:p-6",
          // smooth size reduction on hover
          "text-white shadow-md transition-transform duration-300 ease-out hover:scale-95",
          "flex-shrink-0",
        ].join(" ");

    // For large cards, use different structure with background image
    if (isLarge) {
      const CTAWrapper: any = topic.ctaLink ? Link : "div";
      const ctaProps = topic.ctaLink
        ? {
            href: topic.ctaLink,
            className: "inline-block",
          }
        : {};

      const backgroundStyle = topic.backgroundImageUrl
        ? {
            backgroundImage: `url(${topic.backgroundImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            borderRadius: "1.5rem",
          }
        : {};

      return (
        <div className={baseClasses} style={backgroundStyle}>
          {/* Overlay for better text readability (only if image exists) */}
          {topic.backgroundImageUrl && (
            <div
              className="absolute inset-0 bg-black/30"
              style={{ borderRadius: "1.5rem" }}
            />
          )}

          {/* Fallback gradient if no image */}
          {!topic.backgroundImageUrl && (
            <div
              className={`absolute inset-0 bg-gradient-to-br ${fromClass} ${viaClass} ${toClass}`}
              style={{ borderRadius: "1.5rem" }}
            />
          )}

          {/* Content */}
          <div className="relative z-10 flex h-full flex-col p-8 md:p-10 lg:p-12">
            {/* Title - Top Left, wider on mobile, 1/4 width on desktop with dark shadow */}
            {topic.title && (
              <div className="mb-auto w-2/3 md:w-1/4">
                <h3 className="text-[24px] md:text-[40px] lg:text-[48px] font-semibold leading-none text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] [text-shadow:_0_2px_8px_rgb(0_0_0_/_0.5)]">
                  {topic.title}
                </h3>
                {/* Subtitle - below title if available */}
                {topic.subtitle && (
                  <p className="mt-4 text-[16px] md:text-[18px] lg:text-[20px] leading-tight text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)] [text-shadow:_0_2px_6px_rgb(0_0_0_/_0.5)]">
                    {topic.subtitle}
                  </p>
                )}
              </div>
            )}

            {/* CTA Button - Bottom Right */}
            {topic.ctaLabel && (
              <div className="mt-auto flex justify-end">
                <CTAWrapper {...ctaProps}>
                  <button
                    type="button"
                    className="rounded-full bg-white px-8 py-4 text-lg font-semibold text-gray-900 transition-all hover:bg-gray-100 hover:shadow-lg"
                  >
                    {topic.ctaLabel}
                  </button>
                </CTAWrapper>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Regular small cards (existing logic)
    const Wrapper: any = topic.link ? Link : "div";
    const wrapperProps = topic.link
      ? {
          href: topic.link,
          className: baseClasses,
        }
      : {
          className: baseClasses,
        };

    return (
      <Wrapper key={index} {...wrapperProps}>
        <div className="mt-auto flex flex-col">
          <h3 className="text-[18px] md:text-[40px] lg:text-[48px] font-semibold leading-none">
            {topic.title ? toTitleCase(topic.title) : topic.title}
          </h3>
          {topic.description && (
            <p className="mt-1 text-sm md:text-[15px] lg:text-[16px] leading-snug text-violet-100/90">
              {topic.description}
            </p>
          )}
        </div>
      </Wrapper>
    );
  };

  // Large featured cards section - use Knowledge Hub Cards array if provided, otherwise use first 3 education cards as fallback
  const featuredCards: EducationCard[] =
    knowledgeHubCards && knowledgeHubCards.length > 0
      ? knowledgeHubCards.map((card) => ({
          title: card.title,
          subtitle: card.subtitle,
          backgroundImageUrl: card.backgroundImageUrl,
          gradientFrom: card.gradientFrom,
          gradientVia: card.gradientVia,
          gradientTo: card.gradientTo,
          ctaLabel: card.ctaLabel,
          ctaLink: card.ctaLink,
        }))
      : educationTopics.slice(0, 3); // Fallback: Take first 3 education cards for featured section
  const featuredTrackRef = React.useRef<HTMLDivElement | null>(null);
  const [featuredOffset, setFeaturedOffset] = React.useState(0);
  const [featuredTrackWidth, setFeaturedTrackWidth] = React.useState(0);
  const [featuredInitialOffset, setFeaturedInitialOffset] = React.useState(0);
  const [isFeaturedDragging, setIsFeaturedDragging] = React.useState(false);
  const [featuredDragStartX, setFeaturedDragStartX] = React.useState(0);
  const [featuredDragStartOffset, setFeaturedDragStartOffset] =
    React.useState(0);
  const [featuredScrollDirection, setFeaturedScrollDirection] = React.useState<
    "left" | "right"
  >("left");
  const featuredLastDragXRef = React.useRef(0);
  const [isFeaturedHovered, setIsFeaturedHovered] = React.useState(false);
  const [featuredTouchStartX, setFeaturedTouchStartX] = React.useState(0);
  const [featuredTouchStartY, setFeaturedTouchStartY] = React.useState(0);
  const [featuredTouchStartOffset, setFeaturedTouchStartOffset] =
    React.useState(0);
  const [isFeaturedTouching, setIsFeaturedTouching] = React.useState(false);

  // Duplicate featured cards multiple times for truly endless seamless loop
  // Duplicate 3x to ensure smooth continuous movement without any visible gaps
  const loopedFeaturedCards = [
    ...featuredCards,
    ...featuredCards,
    ...featuredCards,
  ];

  React.useEffect(() => {
    if (!featuredTrackRef.current || typeof window === "undefined") return;
    // Calculate trackWidth the same way as small cards: scrollWidth / 3 (one logical set)
    const width = featuredTrackRef.current.scrollWidth / 3;
    setFeaturedTrackWidth(width);
    // Calculate initial offset to center first card (same as before)
    const containerElement = featuredTrackRef.current.parentElement;
    if (!containerElement) return;
    const containerWidth = containerElement.clientWidth;
    const initialOffset = -(containerWidth * 0.15);
    setFeaturedInitialOffset(initialOffset);
    // Start offset at 0 (same as small cards), initial offset applied in transform
    setFeaturedOffset(0);
  }, [featuredCards.length]);

  // Touch handlers for featured cards mobile swipe
  const handleFeaturedTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setFeaturedTouchStartX(touch.clientX);
    setFeaturedTouchStartY(touch.clientY);
    setFeaturedTouchStartOffset(featuredOffset);
    setIsFeaturedTouching(true);
    featuredLastDragXRef.current = touch.clientX;
  };

  const handleFeaturedTouchMove = (e: React.TouchEvent) => {
    if (!isFeaturedTouching) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - featuredTouchStartX);
    const deltaY = Math.abs(touch.clientY - featuredTouchStartY);

    // Only prevent default if horizontal movement is greater than vertical
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }

    const newDeltaX = touch.clientX - featuredTouchStartX;
    const newOffset = featuredTouchStartOffset + newDeltaX;
    setFeaturedOffset(newOffset);

    // Detect swipe direction
    const currentX = touch.clientX;
    const swipeDelta = currentX - featuredLastDragXRef.current;
    if (Math.abs(swipeDelta) > 2) {
      if (swipeDelta > 0) {
        setFeaturedScrollDirection("right");
      } else if (swipeDelta < 0) {
        setFeaturedScrollDirection("left");
      }
      featuredLastDragXRef.current = currentX;
    }
  };

  const handleFeaturedTouchEnd = () => {
    setIsFeaturedTouching(false);
    // Normalize offset when touch ends
    if (featuredTrackWidth > 0) {
      setFeaturedOffset((prev) => {
        if (prev <= -featuredTrackWidth) return 0;
        if (prev >= 0) return -featuredTrackWidth + 1;
        return prev;
      });
    }
  };

  // Auto-scrolling for large featured cards - exact same logic as small cards
  React.useEffect(() => {
    if (
      !featuredTrackWidth ||
      isFeaturedDragging ||
      isFeaturedHovered ||
      isFeaturedTouching
    )
      return;

    let animationFrame: number;

    const step = () => {
      setFeaturedOffset((prev) => {
        const speed = 0.5; // speed in px per frame (same as small cards)
        const next =
          featuredScrollDirection === "left"
            ? prev - speed // moving left (cards appear from right)
            : prev + speed; // moving right (cards appear from left)

        // Handle loop bounds based on direction - exact same as small cards
        if (featuredScrollDirection === "left") {
          // When we've moved one full set width to the left, reset to 0
          if (Math.abs(next) >= featuredTrackWidth) {
            return 0;
          }
        } else {
          // When we've moved one full set width to the right, reset to -trackWidth
          if (next >= 0) {
            return -featuredTrackWidth;
          }
        }
        return next;
      });
      animationFrame = window.requestAnimationFrame(step);
    };

    animationFrame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [
    featuredTrackWidth,
    isFeaturedDragging,
    isFeaturedHovered,
    isFeaturedTouching,
    featuredScrollDirection,
  ]);

  // Mouse drag handlers for large cards
  const handleFeaturedMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsFeaturedDragging(true);
    setFeaturedDragStartX(e.clientX);
    setFeaturedDragStartOffset(featuredOffset);
    featuredLastDragXRef.current = e.clientX;
  };

  React.useEffect(() => {
    if (!isFeaturedDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - featuredDragStartX;
      const newOffset = featuredDragStartOffset + deltaX;
      setFeaturedOffset(newOffset);

      // Detect drag direction
      const currentX = e.clientX;
      const dragDelta = currentX - featuredLastDragXRef.current;
      if (Math.abs(dragDelta) > 2) {
        if (dragDelta > 0) {
          setFeaturedScrollDirection("right");
        } else if (dragDelta < 0) {
          setFeaturedScrollDirection("left");
        }
        featuredLastDragXRef.current = currentX;
      }
    };

    const handleMouseUp = () => {
      setIsFeaturedDragging(false);
      // Normalize offset to keep it within loop bounds (same as small cards)
      if (featuredTrackWidth > 0) {
        setFeaturedOffset((prev) => {
          // Wrap within [-trackWidth, 0] range
          if (prev <= -featuredTrackWidth) return 0;
          if (prev >= 0) return -featuredTrackWidth + 1;
          return prev;
        });
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isFeaturedDragging,
    featuredDragStartX,
    featuredDragStartOffset,
    featuredTrackWidth,
  ]);

  const handleFeaturedScroll = (direction: "left" | "right") => {
    // Arrow navigation - move one entire card off screen and show next one
    if (!featuredTrackWidth || !featuredTrackRef.current) return;

    // Change scroll direction first
    setFeaturedScrollDirection(direction);

    // Get the actual rendered card width and gap for precise movement
    const firstCardWrapper = featuredTrackRef.current
      .children[0] as HTMLElement;
    if (!firstCardWrapper) return;

    // Use the actual rendered card width (accounts for 70% calculation)
    const cardWidth = firstCardWrapper.offsetWidth;

    // Get the actual gap from computed styles (handles responsive gaps: gap-2 md:gap-3 lg:gap-4)
    const computedGap = window.getComputedStyle(featuredTrackRef.current).gap;
    const gapValue = computedGap.split(" ")[0]; // Get first value (gap can be "8px 12px" for row/column)
    const gap = parseFloat(gapValue) || 16; // Default to 16px if parsing fails

    // Move distance = one full card width + gap (this moves one entire card off screen)
    const moveDistance = cardWidth + gap;

    setFeaturedOffset((prev) => {
      const next =
        direction === "left" ? prev + moveDistance : prev - moveDistance;

      // Wrap within loop bounds for seamless continuation - exact same as small cards
      // Since cards are duplicated 3x, we can safely wrap at trackWidth
      if (direction === "left") {
        if (next >= 0) {
          return next - featuredTrackWidth; // Wrap seamlessly
        }
      } else {
        if (Math.abs(next) >= featuredTrackWidth) {
          return next + featuredTrackWidth; // Wrap seamlessly
        }
      }
      return next;
    });
  };

  return (
    <section className="py-16 md:py-24">
      {/* Heading and description in centered container */}
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
          {heading}
        </h2>
        <p className="mb-12 text-center text-lg text-gray-600">{description}</p>
      </div>

      {/* Large Featured Cards Section (Knowledge Hub Cards) - continuous scrolling like small cards */}
      <div className="mb-2 md:mb-3 lg:mb-4 w-full overflow-hidden">
        <div
          className="relative mx-auto h-[320px] overflow-hidden md:h-[580px]"
          onMouseEnter={() => setIsFeaturedHovered(true)}
          onMouseLeave={() => setIsFeaturedHovered(false)}
        >
          <div className="relative h-full overflow-hidden px-4 md:px-6 lg:px-8">
            <div
              ref={featuredTrackRef}
              className={`flex h-full items-center gap-2 md:gap-3 lg:gap-4 ${isFeaturedDragging ? "cursor-grabbing" : "cursor-grab"} select-none`}
              style={{
                transform: `translateX(calc(${featuredInitialOffset}px + ${featuredOffset}px))`,
                willChange: "transform",
              }}
              onMouseDown={handleFeaturedMouseDown}
              onTouchStart={handleFeaturedTouchStart}
              onTouchMove={handleFeaturedTouchMove}
              onTouchEnd={handleFeaturedTouchEnd}
            >
              {loopedFeaturedCards.map((topic, index) => {
                const cardIndex = index % featuredCards.length;
                return (
                  <div
                    key={index}
                    className="flex-shrink-0"
                    style={{
                      width: "70%",
                    }}
                  >
                    {renderEducationCard(topic, cardIndex, true)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation arrows for featured cards - show on container hover with darker circle background */}
          <button
            type="button"
            aria-label="Previous featured card"
            className={`absolute left-4 top-1/2 z-40 -translate-y-1/2 flex size-10 md:size-12 items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white transition-all ${
              isFeaturedHovered
                ? "opacity-100 hover:bg-black/50 hover:shadow-lg"
                : "opacity-0 pointer-events-none"
            }`}
            onClick={() => handleFeaturedScroll("left")}
          >
            <span
              aria-hidden
              className="inline-block text-lg md:text-xl font-bold"
            >
              ‹
            </span>
          </button>

          <button
            type="button"
            aria-label="Next featured card"
            className={`absolute right-4 top-1/2 z-40 -translate-y-1/2 flex size-10 md:size-12 items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white transition-all ${
              isFeaturedHovered
                ? "opacity-100 hover:bg-black/50 hover:shadow-lg"
                : "opacity-0 pointer-events-none"
            }`}
            onClick={() => handleFeaturedScroll("right")}
          >
            <span
              aria-hidden
              className="inline-block text-lg md:text-xl font-bold"
            >
              ›
            </span>
          </button>
        </div>
      </div>

      {/* Full width cards section */}
      <div
        className="relative w-full"
        onMouseEnter={() => setIsSmallCardsHovered(true)}
        onMouseLeave={() => setIsSmallCardsHovered(false)}
      >
        {/* Left arrow - visible on hover with darker background */}
        <button
          type="button"
          aria-label="Previous education topic"
          className={`absolute left-2 md:left-4 top-1/2 z-10 -translate-y-1/2 flex size-10 md:size-12 items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white transition-all ${
            isSmallCardsHovered
              ? "opacity-100 hover:bg-black/50 hover:shadow-lg"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => handleScroll("left")}
        >
          <span
            aria-hidden
            className="inline-block text-lg md:text-xl font-bold"
          >
            ‹
          </span>
        </button>

        {/* Right arrow - visible on hover with darker background */}
        <button
          type="button"
          aria-label="Next education topic"
          className={`absolute right-2 md:right-4 top-1/2 z-10 -translate-y-1/2 flex size-10 md:size-12 items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white transition-all ${
            isSmallCardsHovered
              ? "opacity-100 hover:bg-black/50 hover:shadow-lg"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => handleScroll("right")}
        >
          <span
            aria-hidden
            className="inline-block text-lg md:text-xl font-bold"
          >
            ›
          </span>
        </button>

        {/* Full width scrollable container */}
        <div className="overflow-hidden w-full px-2 md:px-4 lg:px-6">
          <div
            ref={scrollContainerRef}
            className="overflow-x-hidden pb-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div
              ref={trackRef}
              className={`flex gap-2 md:gap-3 lg:gap-4 ${isDragging ? "cursor-grabbing" : "cursor-grab"} select-none`}
              style={{
                transform: `translateX(${offset}px)`,
                willChange: "transform",
              }}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {loopedTopics.map((topic, index) =>
                renderEducationCard(topic, index)
              )}
            </div>
          </div>
        </div>
      </div>
      <style jsx global>{`
        .overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
